import { useEffect, useRef } from 'react';
import type { Appearance, Sex } from '../api/types';
import { aquecerCabelo, estiloValido, tingirFolhaCabelo } from '../look/cabelo';
import { artePele, peleCss } from '../look/pele';
import {
  arteDa, carregarFolha, desenharQuadro, estiloDaFolha, jsonBarba, jsonCabelo, jsonPersonagem,
  type Folha
} from '../world/spriteAtlas';

const DIR_SE = 1;
/** Recorte do corpo (como o paperdoll do mundo) para preencher o quadro. */
const CORPO_W = 72;
const CORPO_H = 96;

interface Props {
  appearance: Appearance;
  sex: Sex;
  width?: number;
  height?: number;
}

export function Paperdoll({ appearance, sex, width = 168, height = 240 }: Props) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    let cancelled = false;
    const skin = sex === 'female' ? 'human-female' : 'human-male';

    const paint = (corpo: Folha | null, cabelo: Folha | null, barba: Folha | null) => {
      if (cancelled || !ref.current) return;
      const g = ref.current.getContext('2d');
      if (!g) return;
      g.clearRect(0, 0, width, height);
      if (corpo && desenharPilha(g, corpo, cabelo, barba, appearance, sex, width, height)) return;
      desenharBonequinho(g, appearance, sex, width, height);
    };

    void (async () => {
      await aquecerCabelo();
      if (cancelled) return;
      const corpo = await carregarFolha(jsonPersonagem(skin, '', 'idle')).catch(() => null);
      const hairUrl = jsonCabelo(appearance.hairStyle, '', 'idle');
      const beardUrl = sex === 'male' ? jsonBarba(appearance.beardStyle ?? '', '', 'idle') : '';
      const [cabelo, barba] = await Promise.all([
        hairUrl ? carregarFolha(hairUrl).catch(() => null) : Promise.resolve(null),
        beardUrl ? carregarFolha(beardUrl).catch(() => null) : Promise.resolve(null)
      ]);
      paint(corpo, cabelo, barba);
    })();

    return () => { cancelled = true; };
  }, [appearance, sex, width, height]);

  return (
    <canvas
      ref={ref}
      className="paperdoll-create"
      width={width}
      height={height}
      aria-hidden="true"
    />
  );
}

function desenharPilha(
  ctx: CanvasRenderingContext2D,
  corpo: Folha,
  cabelo: Folha | null,
  barba: Folha | null,
  appearance: Appearance,
  sex: Sex,
  w: number,
  h: number
): boolean {
  const art = artePele(corpo, appearance.skinHue, DIR_SE, 0);
  if (!art) return false;
  ctx.imageSmoothingEnabled = false;
  const pad = 12;
  const scale = Math.min((w - pad * 2) / CORPO_W, (h - pad * 2) / CORPO_H);
  const ax = corpo.ancoraX;
  const ay = corpo.ancoraY;
  const corpoY = ay - CORPO_H * 0.48;
  const dx = Math.round(w / 2 - ax * scale);
  const dy = Math.round(h / 2 - corpoY * scale);
  desenharQuadro(ctx, art, dx, dy, corpo.cellW * scale, corpo.cellH * scale);
  if (estiloDaFolha(appearance.hairStyle) && cabelo) {
    blitSobre(ctx, corpo, tingirFolhaCabelo(cabelo, appearance.hairHue), dx, dy, scale);
  }
  if (sex === 'male' && estiloValido(appearance.beardStyle) && barba) {
    blitSobre(ctx, corpo, tingirFolhaCabelo(barba, appearance.beardHue ?? appearance.hairHue), dx, dy, scale);
  }
  return true;
}

function blitSobre(
  ctx: CanvasRenderingContext2D,
  base: Folha,
  camada: Folha,
  dx: number,
  dy: number,
  scale: number
) {
  const art = arteDa(camada, DIR_SE, 0);
  if (!art) return;
  desenharQuadro(
    ctx,
    art,
    dx + (base.ancoraX - camada.ancoraX) * scale,
    dy + (base.ancoraY - camada.ancoraY) * scale,
    camada.cellW * scale,
    camada.cellH * scale
  );
}

function clothColor(hue: number): string {
  const t = Math.min(1, Math.max(0, (hue - 1102) / 47));
  return `hsl(${Math.round(t * 340)} 44% 42%)`;
}

function desenharBonequinho(
  ctx: CanvasRenderingContext2D,
  appearance: Appearance,
  sex: Sex,
  w: number,
  h: number
) {
  const skin = peleCss(appearance.skinHue);
  const hair = clothColor(appearance.hairHue);
  const shirt = clothColor(appearance.shirtHue);
  const pants = clothColor(appearance.pantsHue);
  const beard = appearance.beardHue ? clothColor(appearance.beardHue) : hair;
  const s = Math.min(w / 112, h / 168);
  const cx = w / 2;

  ctx.fillStyle = 'rgba(0,0,0,.28)';
  ctx.fillRect(8 * s, h - 18 * s, w - 16 * s, 8 * s);

  ctx.fillStyle = pants;
  ctx.fillRect(cx - 16 * s, 108 * s, 12 * s, 44 * s);
  ctx.fillRect(cx + 4 * s, 108 * s, 12 * s, 44 * s);

  ctx.fillStyle = shirt;
  ctx.fillRect(cx - 22 * s, 58 * s, 44 * s, 52 * s);
  ctx.fillRect(cx - 34 * s, 60 * s, 12 * s, 36 * s);
  ctx.fillRect(cx + 22 * s, 60 * s, 12 * s, 36 * s);

  ctx.fillStyle = skin;
  ctx.beginPath();
  ctx.ellipse(cx, 38 * s, 16 * s, 18 * s, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillRect(cx - 6 * s, 54 * s, 12 * s, 8 * s);

  drawHair(ctx, appearance.hairStyle, hair, cx, sex, s);
  if (sex === 'male' && appearance.beardStyle && appearance.beardStyle !== 'none') {
    ctx.fillStyle = beard;
    ctx.fillRect(cx - 10 * s, 46 * s, 20 * s, appearance.beardStyle === 'long' ? 16 * s : 8 * s);
  }
}

function drawHair(
  ctx: CanvasRenderingContext2D,
  style: string,
  color: string,
  cx: number,
  sex: Sex,
  s = 1
) {
  ctx.fillStyle = color;
  if (style === 'none') return;
  ctx.beginPath();
  ctx.ellipse(cx, 28 * s, 17 * s, 12 * s, 0, Math.PI, 0);
  ctx.fill();
  if (style === 'long' || style === 'ponytail' || style === 'pigtails') {
    ctx.fillRect(cx + (style === 'pigtails' ? -18 : 10) * s, 28 * s, 8 * s, 36 * s);
    if (style === 'pigtails') ctx.fillRect(cx + 10 * s, 28 * s, 8 * s, 36 * s);
  }
  if (style === 'mohawk') {
    ctx.fillRect(cx - 4 * s, 8 * s, 8 * s, 18 * s);
  }
  if (style === 'buns' || (sex === 'female' && style === 'topknot')) {
    ctx.beginPath();
    ctx.arc(cx - 14 * s, 22 * s, 6 * s, 0, Math.PI * 2);
    ctx.arc(cx + 14 * s, 22 * s, 6 * s, 0, Math.PI * 2);
    ctx.fill();
  }
}
